import React, { useState, useMemo } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Skeleton, Spinner } from "../../Components/Common/LoadingStates";
import { KpiCard, MiniLineChart, Donut, fmt, shortDate } from "../../Components/Common/DashboardWidgets";
import {
  getFeesSummary,
  getFeesMissing,
  getFeeSettlements,
  postFeesSync,
  getSwapkitStatus,
  getRangoStatus,
  postRangoSync,
  postSwapkitSync,
  getDashboardOverview,
  getDashboardMetrics,
  getTransactionAnalytics,
  getPaybisCountsByFlow,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";

document.title = "Fees | BiorGov Admin";

const TIMEFRAMES = [
  { id: "24h", label: "24h" },
  { id: "7d",  label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "all", label: "All" },
];

function fmtUSD(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}
function fmtNum(n) {
  return Number(n || 0).toLocaleString();
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}
function shortHash(s) {
  if (!s) return "—";
  return s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
}

function fmtBps(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toFixed(1)} bps`;
}

// A stable colour per swap integration, so the same provider reads the same hue
// across the roster, the by-source table, and the official-earnings routing bars.
const PROVIDER_COLORS = {
  SwapKit: "#7B7CFF",
  Rango:   "#2BD56F",
  CowSwap: "#f3af4c",
  Jupiter: "#22d3ee",
  Exolix:  "#f472b6",
};
// Fallback palette for any provider name we don't have a fixed colour for
// (e.g. SwapKit's underlying liquidity providers in the official breakdown).
const FALLBACK_PALETTE = ["#7B7CFF", "#2BD56F", "#f3af4c", "#22d3ee", "#f472b6", "#a6a7ff", "#ff9e9e", "#7ee0c0"];
function providerColor(name, i = 0) {
  return PROVIDER_COLORS[name] || FALLBACK_PALETTE[i % FALLBACK_PALETTE.length];
}

function relTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
const STATUS_TINT = {
  CONFIRMED: { bg: "rgba(0,205,82,0.14)",  fg: "var(--bl-success-soft)" },
  PENDING:   { bg: "rgba(243,175,76,0.16)", fg: "#ffd199" },
  FAILED:    { bg: "rgba(255,93,108,0.14)", fg: "#ff8a98" },
  CANCELLED: { bg: "rgba(255,255,255,0.06)", fg: "var(--bl-text-soft)" },
};

/**
 * SwapKit OFFICIAL earnings panel — the authoritative numbers scraped off the
 * partner dashboard (swapkit_revenue_daily / swapkit_swaps), as opposed to the
 * estimated our_fee_usd (20 bps) the rest of the page is built on. Shows the
 * real earnings, the real effective take-rate, and how far our estimate is off.
 */
function SwapkitOfficialPanel({ summary, loading }) {
  const sk = summary?.swapkit_official;

  if (loading) {
    return (
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <Skeleton height={120} />
      </div>
    );
  }
  if (!sk || sk.unavailable || !sk.has_data) {
    return (
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <h3 style={{ margin: "0 0 8px", color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>
          SwapKit — official earnings
        </h3>
        <div style={{ color: "var(--bl-text-soft)", fontSize: 13 }}>
          {sk?.unavailable
            ? "Authoritative SwapKit tables aren't available yet — run the migration, then the scrape."
            : "No scraped SwapKit data for this timeframe yet. Use the “Refresh now” button above to pull it from the dashboard."}
        </div>
      </div>
    );
  }

  const revenue = Number(sk.revenue_usd || 0);
  const estimate = Number(sk.our_estimate_usd || 0);
  const delta = Number(sk.estimate_vs_official_usd || 0); // estimate − official
  const overEst = delta > 0.01;
  const underEst = delta < -0.01;
  const deltaColor = Math.abs(delta) < 0.01 ? "#5be58a" : overEst ? "#f3af4c" : "#a6a7ff";
  const deltaLabel = Math.abs(delta) < 0.01
    ? "Estimate matches official"
    : overEst ? "We over-estimate our cut" : "We under-estimate our cut";

  const bps = sk.effective_bps;
  const bpsColor = bps == null ? "var(--bl-text-soft)" : bps < 19 ? "#f3af4c" : "#5be58a";
  const byProvider = sk.by_provider || [];
  const totalProv = byProvider.reduce((a, b) => a + Number(b.revenue_usd || 0), 0);

  return (
    <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem", border: "1px solid rgba(0,205,82,0.25)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>
          SwapKit — official earnings <span style={{ fontSize: 11, color: "#5be58a", fontWeight: 600 }}>· authoritative</span>
        </h3>
        <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>
          {sk.swap_count != null ? `${fmtNum(sk.swap_count)} swaps · ` : ""}scraped {fmtDate(sk.last_scraped_at)}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem", marginBottom: byProvider.length ? 16 : 0 }}>
        <div>
          <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Official earnings</div>
          <div style={{ fontSize: "1.5rem", color: "#5be58a", fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{fmtUSD(revenue)}</div>
          <div style={{ fontSize: 11, color: "var(--bl-text-soft)", marginTop: 2 }}>what SwapKit actually paid us</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Effective take-rate</div>
          <div style={{ fontSize: "1.5rem", color: bpsColor, fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{fmtBps(bps)}</div>
          <div style={{ fontSize: 11, color: "var(--bl-text-soft)", marginTop: 2 }}>vs 20.0 bps assumed</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Our estimate (20 bps)</div>
          <div style={{ fontSize: "1.5rem", color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{fmtUSD(estimate)}</div>
          <div style={{ fontSize: 11, color: "var(--bl-text-soft)", marginTop: 2 }}>from tagged SwapKit swaps</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Estimate vs official</div>
          <div style={{ fontSize: "1.5rem", color: deltaColor, fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
            {delta >= 0 ? "+" : ""}{fmtUSD(delta)}
          </div>
          <div style={{ fontSize: 11, color: deltaColor, marginTop: 2 }}>{deltaLabel}</div>
        </div>
      </div>

      {byProvider.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>
            Official revenue by routed provider <span style={{ textTransform: "none", letterSpacing: 0, opacity: 0.7 }}>· colour-coded by where the swap routed</span>
          </div>
          {byProvider.map((row, i) => {
            const v = Number(row.revenue_usd || 0);
            const pct = totalProv > 0 ? (v / totalProv) * 100 : 0;
            const c = providerColor(row.provider, i);
            return (
              <div key={row.provider} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 120, fontSize: 12, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: c, flexShrink: 0 }} />
                  {row.provider}
                </div>
                <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: c }} />
                </div>
                <div style={{ width: 110, textAlign: "right", color: c, fontSize: 13, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmtUSD(v)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * SwapKit scrape freshness / reminder banner.
 *
 * Reads GET /fees/swapkit/status. Green & quiet when the daily scrape is fresh;
 * amber/red when it's stale or the saved dashboard session has expired, with a
 * "Refresh now" button (POST /fees/swapkit/sync) for right after re-exporting a
 * session. The whole point: tell the user when to go re-export their SwapKit
 * session so the authoritative earnings data keeps flowing.
 */
function SwapkitFreshnessBanner() {
  const statusReq = useCachedRequest({
    key: "fees:swapkit:status",
    fetcher: () => getSwapkitStatus().then((res) => res || {}).catch(() => null),
    deps: [],
  });
  const status = statusReq.data;
  const [refreshing, setRefreshing] = useState(false);
  const [note, setNote] = useState("");

  // Don't render anything until we have a status (avoid a flash/empty box).
  if (!status) return null;

  const sessionExpired = !!status.session_expired;
  const neverRun = status.status === "never_run";
  const noSession = status.has_session === false;
  const stale = !!status.is_stale;

  // Tone: red for expired/no-session/error, amber for merely stale/never-run, green when fresh.
  const isError = status.status === "error";
  const tone =
    sessionExpired || noSession || isError ? "danger" :
    stale || neverRun ? "warn" :
    "ok";

  const palette = {
    ok:     { bg: "rgba(0,205,82,0.08)",   border: "rgba(0,205,82,0.30)",   fg: "#5be58a", icon: "ri-check-line" },
    warn:   { bg: "rgba(243,175,76,0.10)", border: "rgba(243,175,76,0.32)", fg: "#f3af4c", icon: "ri-time-line" },
    danger: { bg: "rgba(255,110,110,0.10)",border: "rgba(255,110,110,0.32)",fg: "#ff9e9e", icon: "ri-error-warning-line" },
  }[tone];

  const lastSuccess = status.last_success_at ? fmtDate(status.last_success_at) : "never";
  const ageHrs = status.age_hours;
  const ageLabel =
    ageHrs == null ? "" :
    ageHrs < 1 ? "just now" :
    ageHrs < 48 ? `${Math.round(ageHrs)}h ago` :
    `${Math.round(ageHrs / 24)}d ago`;

  let headline;
  let detail;
  if (noSession) {
    headline = "SwapKit session not configured";
    detail = "Set SWAPKIT_COOKIE / SWAPKIT_SESSION_FILE (export a logged-in dashboard.swapkit.dev session) so the daily scrape can run.";
  } else if (sessionExpired) {
    headline = "SwapKit session expired — re-export needed";
    detail = "The daily scrape can't reach the dashboard. Log in to dashboard.swapkit.dev, re-export your session, then hit Refresh.";
  } else if (isError) {
    headline = "SwapKit scrape failing";
    detail = status.latest_run?.error_message || "The last scrape hit an error (not a session issue). Check the server logs.";
  } else if (neverRun) {
    headline = "SwapKit scrape hasn't run yet";
    detail = "The daily scrape will populate authoritative earnings. You can run it now.";
  } else if (stale) {
    headline = "SwapKit data is stale";
    detail = `Last successful scrape ${ageLabel}. It may need a refresh or a re-exported session.`;
  } else {
    headline = "SwapKit data is fresh";
    detail = `Last scrape ${ageLabel} · ${fmtNum(status.latest_run?.swap_rows)} swaps, ${fmtNum(status.latest_run?.revenue_rows)} revenue rows.`;
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    setNote("");
    try {
      const res = await postSwapkitSync();
      const body = res || {};
      if (body.status === "ok") {
        setNote(`Scraped ${fmtNum(body.swapRows)} swaps`);
      } else {
        setNote(body.error || `Scrape ${body.status || "failed"}`);
      }
      invalidateCache("fees:swapkit");
      await statusReq.refetch();
    } catch (err) {
      setNote(typeof err === "string" ? err : err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div
      className="bl-glass"
      style={{
        marginBottom: "1.25rem", padding: "0.85rem 1rem",
        background: palette.bg, border: `1px solid ${palette.border}`,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}
    >
      <i className={palette.icon} style={{ color: palette.fg, fontSize: 18 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: palette.fg, fontSize: 13, fontWeight: 700 }}>{headline}</div>
        <div style={{ color: "var(--bl-text-soft)", fontSize: 12, marginTop: 2 }}>
          {detail} <span style={{ opacity: 0.7 }}>· last success: {lastSuccess}</span>
        </div>
      </div>
      {note && (
        <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>{note}</span>
      )}
      <button
        onClick={handleRefresh}
        disabled={refreshing || noSession}
        style={{
          padding: "0.5rem 0.8rem", borderRadius: 8,
          background: "rgba(123,124,255,0.16)", border: "1px solid rgba(123,124,255,0.3)",
          color: "#a6a7ff", fontSize: 12, fontWeight: 600,
          cursor: refreshing || noSession ? "not-allowed" : "pointer", opacity: refreshing || noSession ? 0.6 : 1,
          display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
        }}
        title={noSession ? "Configure a SwapKit session first" : "Run the SwapKit scrape now"}
      >
        {refreshing ? <Spinner size={12} /> : <i className="ri-refresh-line" />}
        {refreshing ? "Refreshing…" : "Refresh now"}
      </button>
    </div>
  );
}

/**
 * Rango sync freshness banner.
 *
 * Deliberately mirrors SwapkitFreshnessBanner so the Fees page reads the same
 * for both providers, but the failure vocabulary differs because the mechanism
 * does: SwapKit replays a scraped browser session that expires, Rango uses a
 * documented API key. So there is no "re-export your session" state here — a
 * rejected key says "check RANGO_API_KEY / RANGO_SECRET_TOKEN" instead.
 *
 * Reads GET /fees/rango/status; "Refresh now" posts /fees/rango/sync.
 */
function RangoFreshnessBanner() {
  const statusReq = useCachedRequest({
    key: "fees:rango:status",
    fetcher: () => getRangoStatus().then((res) => res || {}).catch(() => null),
    deps: [],
  });
  const status = statusReq.data;
  const [refreshing, setRefreshing] = useState(false);
  const [note, setNote] = useState("");

  if (!status) return null;

  const notConfigured = status.is_configured === false;
  const authFailed = !!status.auth_failed;
  const neverRun = status.status === "never_run";
  const stale = !!status.is_stale;
  const isError = status.status === "error";
  const rateLimited = status.status === "rate_limited";

  const tone =
    notConfigured || authFailed || isError ? "danger" :
    stale || neverRun || rateLimited ? "warn" :
    "ok";

  const palette = {
    ok:     { bg: "rgba(0,205,82,0.08)",   border: "rgba(0,205,82,0.30)",   fg: "#5be58a", icon: "ri-check-line" },
    warn:   { bg: "rgba(243,175,76,0.10)", border: "rgba(243,175,76,0.32)", fg: "#f3af4c", icon: "ri-time-line" },
    danger: { bg: "rgba(255,110,110,0.10)",border: "rgba(255,110,110,0.32)", fg: "#ff9e9e", icon: "ri-error-warning-line" },
  }[tone];

  const lastSuccess = status.last_success_at ? fmtDate(status.last_success_at) : "never";
  const ageHrs = status.age_hours;
  const ageLabel =
    ageHrs == null ? "" :
    ageHrs < 1 ? "just now" :
    ageHrs < 48 ? `${Math.round(ageHrs)}h ago` :
    `${Math.round(ageHrs / 24)}d ago`;

  let headline;
  let detail;
  if (notConfigured) {
    headline = "Rango API not configured";
    detail = "Set RANGO_API_KEY and RANGO_SECRET_TOKEN so the daily sync can pull Rango's own per-swap fees.";
  } else if (authFailed) {
    headline = "Rango rejected our credentials";
    detail = "The key or security token is wrong or revoked. Check RANGO_API_KEY / RANGO_SECRET_TOKEN, then hit Refresh.";
  } else if (isError) {
    headline = "Rango sync failing";
    detail = status.latest_run?.error_message || "The last sync hit an error. Check the server logs.";
  } else if (rateLimited) {
    headline = "Rango is rate-limiting us";
    detail = "The last sync was throttled. It will retry tonight; raise RANGO_SYNC_DELAY_MS if it persists.";
  } else if (neverRun) {
    headline = "Rango sync hasn't run yet";
    detail = "Run it now to pull Rango's authoritative per-swap fees.";
  } else if (stale) {
    headline = "Rango data is stale";
    detail = `Last successful sync ${ageLabel}.`;
  } else {
    headline = "Rango data is fresh";
    detail = `Last sync ${ageLabel} · ${fmtNum(status.latest_run?.swap_rows)} swaps across ${fmtNum(status.latest_run?.addresses_queried)} wallets.`;
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    setNote("");
    try {
      const body = (await postRangoSync()) || {};
      if (body.status === "ok") {
        setNote(`Synced ${fmtNum(body.rows)} swaps`);
      } else {
        setNote(body.error || `Sync ${body.status || "failed"}`);
      }
      invalidateCache("fees:rango");
      await statusReq.refetch();
    } catch (err) {
      setNote(typeof err === "string" ? err : err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div
      className="bl-glass"
      style={{
        marginBottom: "1.25rem", padding: "0.85rem 1rem",
        background: palette.bg, border: `1px solid ${palette.border}`,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}
    >
      <i className={palette.icon} style={{ color: palette.fg, fontSize: 18 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: palette.fg, fontSize: 13, fontWeight: 700 }}>{headline}</div>
        <div style={{ color: "var(--bl-text-soft)", fontSize: 12, marginTop: 2 }}>
          {detail} <span style={{ opacity: 0.7 }}>· last success: {lastSuccess}</span>
        </div>
      </div>
      {note && <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>{note}</span>}
      <button
        onClick={handleRefresh}
        disabled={refreshing || notConfigured}
        style={{
          padding: "0.5rem 0.8rem", borderRadius: 8,
          background: "rgba(123,124,255,0.16)", border: "1px solid rgba(123,124,255,0.3)",
          color: "#a6a7ff", fontSize: 12, fontWeight: 600,
          cursor: refreshing || notConfigured ? "not-allowed" : "pointer",
          opacity: refreshing || notConfigured ? 0.6 : 1,
          display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
        }}
        title={notConfigured ? "Set RANGO_API_KEY / RANGO_SECRET_TOKEN first" : "Run the Rango sync now"}
      >
        {refreshing ? <Spinner size={12} /> : <i className="ri-refresh-line" />}
        {refreshing ? "Refreshing…" : "Refresh now"}
      </button>
    </div>
  );
}

const Fees = ({ embedded = false, view = "fees" } = {}) => {
  const isAnalytics = view === "analytics";
  const [timeframe, setTimeframe] = useState("30d");
  const [tab, setTab] = useState("settlements"); // "settlements" | "missing"
  const [settlementsPage, setSettlementsPage] = useState(1);
  const [missingPage, setMissingPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState("");
  // Which provider tile is expanded into its detail panel (null = none). SwapKit
  // opens its authoritative official-earnings module; others open a placeholder.
  const [openProvider, setOpenProvider] = useState(null);

  const PAGE_SIZE = 25;

  const summaryReq = useCachedRequest({
    key: `fees:summary:${timeframe}`,
    fetcher: () => getFeesSummary(timeframe).then((res) => res || {}),
    deps: [timeframe],
  });
  const summary = summaryReq.data;
  const summaryLoading = summaryReq.loading;

  // ── App-activity fetch (fixed window, NOT driven by the timeframe above) ──
  // Mirrors the old Dashboard "Operational snapshot": transactions, the 14-day
  // graph, the type distribution, and the Paybis on/off-ramp + cards counts.
  // These come from non-timeframe endpoints, so they intentionally ignore the
  // fee timeframe selector.
  const activity = useCachedRequest({
    key: "fees:activity:combined",
    fetcher: async () => {
      const [ov, m, a, pb] = await Promise.all([
        getDashboardOverview().catch(() => ({})),
        getDashboardMetrics().catch(() => ({})),
        getTransactionAnalytics().catch(() => ({})),
        getPaybisCountsByFlow().catch(() => ({})),
      ]);
      return {
        overview: ov || {},
        metrics: m || {},
        analytics: a || {},
        paybis: { on_ramp: pb?.on_ramp || 0, off_ramp: pb?.off_ramp || 0 },
      };
    },
  });
  const activityLoading = activity.loading;
  const actOverview  = activity.data?.overview || null;
  const actMetrics   = activity.data?.metrics || null;
  const actAnalytics = activity.data?.analytics || null;
  const actPaybis    = activity.data?.paybis || { on_ramp: 0, off_ramp: 0 };
  const totalTx = actOverview?.total_transactions ?? actOverview?.transaction_statistics?.total_transactions ?? actMetrics?.total_transactions_count ?? 0;

  const activitySeries = useMemo(() => (actAnalytics?.time_series || []).map((row) => ({
    label: shortDate(row.date),
    y: (row.send || 0) + (row.swap || 0) + (row.ramp || 0),
  })), [actAnalytics]);

  const activityDonut = useMemo(() => {
    const dist = actAnalytics?.distribution || {};
    return [
      { label: "Send",     value: dist.SEND    || 0, color: "#5758FC" },
      { label: "Swap",     value: dist.SWAP    || 0, color: "#7B7CFF" },
      { label: "On-ramp",  value: dist.ONRAMP  || 0, color: "#2BD56F" },
      { label: "Off-ramp", value: dist.OFFRAMP || 0, color: "#f3af4c" },
    ].filter((s) => s.value > 0);
  }, [actAnalytics]);

  const settlementsReq = useCachedRequest({
    key: `fees:settlements:${settlementsPage}`,
    fetcher: () => getFeeSettlements({ limit: PAGE_SIZE, offset: (settlementsPage - 1) * PAGE_SIZE })
      .then((res) => res || {})
      .catch(() => ({ rows: [], total: 0 })),
    deps: [settlementsPage],
  });
  const settlements = settlementsReq.data?.rows || [];
  const settlementsTotal = Number.isFinite(settlementsReq.data?.total) ? settlementsReq.data.total : 0;
  const settlementsLoading = settlementsReq.loading;

  const missingReq = useCachedRequest({
    key: `fees:missing:${missingPage}`,
    fetcher: () => getFeesMissing({ limit: PAGE_SIZE, offset: (missingPage - 1) * PAGE_SIZE })
      .then((res) => res || {})
      .catch(() => ({ rows: [], total: 0 })),
    deps: [missingPage],
  });
  const missing = missingReq.data?.rows || [];
  const missingTotal = Number.isFinite(missingReq.data?.total) ? missingReq.data.total : 0;
  const missingLoading = missingReq.loading;

  const handleSyncWallet = async () => {
    setSyncing(true);
    setSyncStatus(null);
    setError("");
    try {
      const res = await postFeesSync();
      const body = res || {};
      const insertedTotal = Object.values(body.by_network || {}).reduce(
        (a, n) => a + (Number(n?.inserted) || 0), 0
      );
      setSyncStatus({ insertedTotal, body });
      // The sync mutates fee_settlements; drop cached views so the next
      // refetch (and the user's next visit) sees the new rows.
      invalidateCache("fees:");
      await Promise.all([summaryReq.refetch(), settlementsReq.refetch()]);
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const byNetwork = summary?.by_network || [];
  const bySource = summary?.by_source || [];
  const totalSourceVolume = bySource.reduce((a, b) => a + Number(b.volume_usd || 0), 0);

  // If the total swap volume exceeds the sum of the per-source breakdown, the
  // difference is swaps with no recorded swap_partner (unattributed).
  const totalVolume = Number(summary?.total_volume_usd || 0);
  const unattributedVolume = Math.max(0, totalVolume - totalSourceVolume);

  // For SwapKit the *official* scraped earnings are the correct fee number — the
  // per-swap our_fee_usd estimate is short by the first few swaps we did before
  // the SwapKit dashboard was wired up (a fixed ~$2 gap that never closes). So
  // wherever we show SwapKit's fee, prefer the official revenue when we have it.
  const skOfficial = summary?.swapkit_official;
  const swapkitOfficialFee = skOfficial && skOfficial.has_data ? Number(skOfficial.revenue_usd || 0) : null;

  // Every routing provider we support, shown as a first-class tile even when it
  // has no swaps yet — so the dashboard isn't SwapKit-only and new providers
  // are visibly "tracked, awaiting data". `earns` = we collect an affiliate fee
  // today; `rate` is the net we keep (CowSwap nets 0.15% because CoW retains a
  // 25% service fee out of our 0.20% partnerFee). The others send volume but no
  // fee until the mobile app sets an affiliate.
  const KNOWN_PROVIDERS = [
    { name: "SwapKit", earns: true,  rate: "0.20%" },
    { name: "Rango",   earns: true,  rate: "0.20%" },
    { name: "CowSwap", earns: true,  rate: "0.15%" },
    { name: "Jupiter", earns: false },
    { name: "Exolix",  earns: false },
  ];
  const providerRows = KNOWN_PROVIDERS.map((p) => {
    const row = bySource.find((s) => s.swap_partner === p.name) || {};
    const isSwapkit = p.name === "SwapKit";
    const officialFee = isSwapkit && swapkitOfficialFee != null;
    return {
      name: p.name,
      earns: p.earns,
      rate: p.rate,
      volume: Number(row.volume_usd || 0),
      ourFee: officialFee ? swapkitOfficialFee : Number(row.our_fee_usd || 0),
      officialFee,
      partnerFee: Number(row.partner_fee_usd || 0),
      swaps: Number(row.swap_count || 0),
    };
  });
  // Total swap volume across every source — the headline number for the by-source block.
  const totalSwapVolume = Number(summary?.total_volume_usd || 0);

  // "Earnings by route" — one consolidated view of what every integration earns,
  // broken down by the route the swap actually took. SwapKit splits into its
  // routed sub-providers (the authoritative official by_provider numbers); the
  // other integrations each count as their own route. Routes that share a name
  // merge into one row (with both feeding integrations listed under "via"), so
  // you can see when two integrations lean on the same underlying route.
  const routeRows = (() => {
    const map = new Map();
    const add = (route, fee, via) => {
      if (!route) return;
      const cur = map.get(route) || { route, fee: 0, vias: [] };
      cur.fee += fee;
      if (via && !cur.vias.includes(via)) cur.vias.push(via);
      map.set(route, cur);
    };
    const skByProvider = skOfficial?.by_provider || [];
    if (skByProvider.length) {
      skByProvider.forEach((r) => add(r.provider, Number(r.revenue_usd || 0), "SwapKit"));
    } else if (swapkitOfficialFee != null) {
      // Official total exists but no per-route breakdown scraped — show it as one route.
      add("SwapKit", swapkitOfficialFee, "SwapKit");
    }
    bySource
      .filter((s) => s.swap_partner !== "SwapKit")
      .forEach((s) => add(s.swap_partner, Number(s.our_fee_usd || 0), s.swap_partner));
    return Array.from(map.values()).sort((a, b) => b.fee - a.fee);
  })();
  const totalRouteFee = routeRows.reduce((a, r) => a + r.fee, 0);

  // Per-network integration breakdown (network -> [{swap_partner, volume, ...}]),
  // so each "Fee revenue by network" row can be colour-segmented by integration.
  const byNetworkSource = summary?.by_network_source || [];
  const networkSegments = (() => {
    const map = new Map();
    byNetworkSource.forEach((r) => {
      const arr = map.get(r.network) || [];
      arr.push(r);
      map.set(r.network, arr);
    });
    return map;
  })();
  // Distinct integrations seen across networks — drives the colour legend.
  const networkProviders = Array.from(new Set(byNetworkSource.map((r) => r.swap_partner))).filter(Boolean);

  return (
    <div style={embedded ? { minWidth: 0 } : { padding: "clamp(1rem, 2.5vw, 1.75rem)", maxWidth: 1400, margin: "0 auto" }}>
      {!embedded && <BreadCrumb title={isAnalytics ? "Analytics" : "Fees"} pageTitle="Money" />}
      <style>{`@media (max-width: 768px) { .fees-activity-grid { grid-template-columns: 1fr !important; } }`}</style>

      {error && (
        <div style={{
          marginBottom: "1rem", padding: "0.75rem 1rem", borderRadius: 8,
          background: "rgba(255,110,110,0.1)", border: "1px solid rgba(255,110,110,0.3)",
          color: "#ff9e9e", fontSize: 13,
        }}>{error}</div>
      )}

      {/* ── SwapKit scrape freshness / re-export reminder (Fees view) ── */}
      {!isAnalytics && <SwapkitFreshnessBanner />}
      {!isAnalytics && <RangoFreshnessBanner />}

      {/* ── Timeframe selector + Sync button (Fees view only) ────────── */}
      {!isAnalytics && (
      <div className="bl-glass" style={{ padding: "0.85rem 1rem", marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Timeframe:</span>
        <div role="tablist" style={{ display: "inline-flex", background: "rgba(255,255,255,0.04)", border: "1px solid var(--bl-line)", borderRadius: 8, padding: 2 }}>
          {TIMEFRAMES.map((t) => {
            const active = t.id === timeframe;
            return (
              <button
                key={t.id}
                onClick={() => setTimeframe(t.id)}
                style={{
                  border: "none",
                  background: active ? "var(--bl-accent)" : "transparent",
                  color: active ? "#fff" : "var(--bl-text-soft)",
                  fontSize: 12, fontWeight: 600,
                  padding: "6px 12px", borderRadius: 6,
                  cursor: "pointer",
                }}
              >{t.label}</button>
            );
          })}
        </div>
        {!isAnalytics && (
          <button
            onClick={handleSyncWallet}
            disabled={syncing}
            style={{
              marginLeft: "auto",
              padding: "0.55rem 0.85rem", borderRadius: 8,
              background: "rgba(123,124,255,0.16)", border: "1px solid rgba(123,124,255,0.3)",
              color: "#a6a7ff", fontSize: 12, fontWeight: 600,
              cursor: syncing ? "not-allowed" : "pointer", opacity: syncing ? 0.6 : 1,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
            title="Pull the latest USDC payouts from Moralis now"
          >
            {syncing ? <Spinner size={12} /> : <i className="ri-refresh-line" />}
            {syncing ? "Syncing wallet…" : "Sync wallet now"}
          </button>
        )}
        {!isAnalytics && syncStatus && (
          <span style={{ fontSize: 12, color: syncStatus.insertedTotal > 0 ? "#5be58a" : "var(--bl-text-soft)" }}>
            {syncStatus.insertedTotal > 0
              ? `+${syncStatus.insertedTotal} new settlement${syncStatus.insertedTotal === 1 ? "" : "s"}`
              : "No new payouts"}
          </span>
        )}
      </div>
      )}

      {/* ── Fee reconciliation: did the wallet actually receive our 0.2%? ──
           expected = per-swap 0.2% estimate; received = USDC into the fee
           wallet; delta = received − expected (positive = wallet ahead). ──── */}
      {!isAnalytics && summary && (
        <div className="bl-glass" style={{ padding: "1.1rem 1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>Fee reconciliation</h3>
            <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>{timeframe === "all" ? "all time" : `last ${timeframe}`}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem" }}>
            {[
              { label: "Expected (0.2%)", value: Number(summary.expected_fee_usd || 0), color: "var(--bl-text-strong)", hint: "Sum of our per-swap 0.2% estimate" },
              { label: "Received (wallet)", value: Number(summary.received_fee_usd || 0), color: "#5be58a", hint: "USDC settled into the fee wallet" },
              (() => {
                const delta = Number(summary.reconciliation_delta_usd || 0);
                return {
                  label: "Delta",
                  value: delta,
                  color: delta >= 0 ? "#5be58a" : "#ff9e9e",
                  prefix: delta >= 0 ? "+" : "",
                  hint: delta >= 0 ? "Wallet ahead of estimate" : "Wallet behind estimate",
                };
              })(),
            ].map((s) => (
              <div key={s.label}>
                <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                <div style={{ fontSize: "1.35rem", color: s.color, fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
                  {summaryLoading ? "…" : `${s.prefix || ""}${fmtUSD(s.value)}`}
                </div>
                <div style={{ fontSize: 11, color: "var(--bl-text-soft)", marginTop: 2 }}>{s.hint}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ════════ FEES VIEW ════════ */}
      {/* ── Swap providers roster — every integration, always shown ───── */}
      {!isAnalytics && (
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>Swap providers</h3>
          <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>{timeframe === "all" ? "all time" : `last ${timeframe}`}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--bl-text-soft)", marginBottom: 14, lineHeight: 1.5 }}>
          Every routing provider the app integrates with — shown here even before any
          swaps flow through it, so each one is visibly tracked. We collect our 0.20%
          on <strong style={{ color: "var(--bl-text-strong)" }}>SwapKit &amp; Rango</strong> today;
          the rest are routed for volume only until an affiliate fee is configured in the mobile app.
          SwapKit's fee is its <strong style={{ color: "var(--bl-text-strong)" }}>official scraped earnings</strong>.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
          {providerRows.map((p) => {
            const hasSwaps = p.swaps > 0;
            const c = providerColor(p.name);
            const isOpen = openProvider === p.name;
            const badge = !hasSwaps
              ? { text: "No swaps yet", bg: "rgba(255,255,255,0.05)", fg: "var(--bl-text-soft)" }
              : p.earns
                ? { text: `Earning ${p.rate}`, bg: "rgba(0,205,82,0.12)", fg: "#5be58a" }
                : { text: "Volume only", bg: "rgba(243,175,76,0.12)", fg: "#f3af4c" };
            return (
              <div
                key={p.name}
                role="button"
                tabIndex={0}
                onClick={() => setOpenProvider(isOpen ? null : p.name)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenProvider(isOpen ? null : p.name); } }}
                style={{
                  background: isOpen ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.02)",
                  border: isOpen ? `1px solid ${c}` : "1px solid var(--bl-line)",
                  borderLeft: `3px solid ${c}`,
                  borderRadius: 12,
                  padding: "1rem 1.1rem",
                  opacity: hasSwaps ? 1 : 0.72,
                  cursor: "pointer",
                  boxShadow: isOpen ? `0 0 0 1px ${c}55` : "none",
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                  <span style={{ color: "#fff", fontWeight: 700, fontSize: "1rem", fontFamily: "var(--bl-font-display)", display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: c, flexShrink: 0 }} />
                    {p.name}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 999,
                    background: badge.bg, color: badge.fg, whiteSpace: "nowrap",
                  }}>{badge.text}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>Volume</span>
                  <span style={{ fontSize: 13, color: "var(--bl-text-strong)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(p.volume)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>Swaps</span>
                  <span style={{ fontSize: 13, color: "#fff", fontVariantNumeric: "tabular-nums" }}>{fmtNum(p.swaps)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>
                    Our fee{p.officialFee && <span style={{ color: "#5be58a", marginLeft: 4 }}>· official</span>}
                  </span>
                  <span style={{ fontSize: 13, color: p.ourFee > 0 ? "#5be58a" : "var(--bl-text-soft)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(p.ourFee)}</span>
                </div>
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed var(--bl-line)", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 11, color: isOpen ? c : "var(--bl-text-soft)", fontWeight: 600 }}>
                  <span>{p.name === "SwapKit" ? "Official earnings" : "Earnings detail"}</span>
                  <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* ── Per-provider detail — opens when you click a provider tile above.
           SwapKit shows its authoritative official-earnings module; the others
           are placeholders until each provider's own module is wired up. ──── */}
      {!isAnalytics && openProvider === "SwapKit" && (
        <SwapkitOfficialPanel summary={summary} loading={summaryLoading} />
      )}
      {!isAnalytics && openProvider && openProvider !== "SwapKit" && (() => {
        const pr = providerRows.find((p) => p.name === openProvider) || {};
        const c = providerColor(openProvider);
        return (
          <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem", border: `1px solid ${c}55` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: c, flexShrink: 0 }} />
                {openProvider} — earnings detail
              </h3>
              <button
                onClick={() => setOpenProvider(null)}
                style={{ background: "none", border: "none", color: "var(--bl-text-soft)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
                title="Close"
              ><i className="ri-close-line" /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Volume</div>
                <div style={{ fontSize: "1.35rem", color: "var(--bl-text-strong)", fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{fmtUSD(pr.volume)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Our fee</div>
                <div style={{ fontSize: "1.35rem", color: pr.ourFee > 0 ? "#5be58a" : "var(--bl-text-soft)", fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{fmtUSD(pr.ourFee)}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Swaps</div>
                <div style={{ fontSize: "1.35rem", color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{fmtNum(pr.swaps)}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--bl-text-soft)", lineHeight: 1.5 }}>
              {pr.earns
                ? openProvider === "CowSwap"
                  ? `We set a 0.20% partnerFee in the CoW order's appData; CoW retains a 25% service fee (CIP-75), so we net ${pr.rate} on ${openProvider} swaps. `
                  : `We collect our ${pr.rate} affiliate fee on ${openProvider} swaps. `
                : `${openProvider} swaps are routed for volume only — no affiliate fee is configured in the mobile app yet, so our fee stays $0. `}
              A dedicated earnings module for {openProvider} (like SwapKit's official panel) is coming soon. Its routed earnings already feed the <strong style={{ color: "var(--bl-text-strong)" }}>Earnings by route</strong> board below.
            </div>
          </div>
        );
      })()}

      {/* ── Volume + fees by swap source (with total volume) ──────────── */}
      {!isAnalytics && (
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>Volume + fees by swap source</h3>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total volume · {timeframe === "all" ? "all time" : `last ${timeframe}`}</div>
            <div style={{ fontSize: "1.25rem", color: "var(--bl-accent-light, #a6a7ff)", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(totalSwapVolume)}</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "var(--bl-text-soft)", marginBottom: 12, lineHeight: 1.5 }}>
          Every routing provider that sends us swaps shows up here — SwapKit, Rango, CowSwap, Jupiter, Exolix.
          We earn our 0.20% on <strong style={{ color: "var(--bl-text-strong)" }}>SwapKit &amp; Rango</strong> and
          0.15% net on <strong style={{ color: "var(--bl-text-strong)" }}>CowSwap</strong> (after CoW&apos;s 25% service fee) today;
          the rest show volume with a $0 fee until an affiliate fee is configured in the mobile app.
          {unattributedVolume > 0 && (
            <> {fmtUSD(unattributedVolume)} of the total is from swaps with no recorded source.</>
          )}
        </div>
        {summaryLoading ? (
          <Skeleton height={120} />
        ) : bySource.length === 0 ? (
          <div style={{ padding: "1rem", textAlign: "center", color: "var(--bl-text-soft)", fontSize: 13 }}>
            No swaps with a recorded source in this period.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <div style={{ width: 96 }}>Source</div>
              <div style={{ flex: 1 }}>Volume share</div>
              <div style={{ width: 110, textAlign: "right" }}>Volume</div>
              <div style={{ width: 110, textAlign: "right" }}>Our fee</div>
              <div style={{ width: 110, textAlign: "right" }}>Partner fee</div>
              <div style={{ width: 60, textAlign: "right" }}>Swaps</div>
            </div>
            {bySource
              .slice()
              .sort((a, b) => Number(b.swap_count) - Number(a.swap_count))
              .map((row) => {
                const vol = Number(row.volume_usd || 0);
                const pct = totalSourceVolume > 0 ? (vol / totalSourceVolume) * 100 : 0;
                const c = providerColor(row.swap_partner);
                const isSwapkit = row.swap_partner === "SwapKit";
                const ourFee = isSwapkit && swapkitOfficialFee != null ? swapkitOfficialFee : Number(row.our_fee_usd || 0);
                return (
                  <div key={row.swap_partner} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 96, fontSize: 12, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 3, background: c, flexShrink: 0 }} />
                      {row.swap_partner}
                    </div>
                    <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: c }} />
                    </div>
                    <div style={{ width: 110, textAlign: "right", color: "var(--bl-text-strong)", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(vol)}</div>
                    <div style={{ width: 110, textAlign: "right", color: "#5be58a", fontSize: 13, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                      {fmtUSD(ourFee)}{isSwapkit && swapkitOfficialFee != null && <span style={{ fontSize: 9, color: "var(--bl-text-soft)", marginLeft: 3 }}>official</span>}
                    </div>
                    <div style={{ width: 110, textAlign: "right", color: "var(--bl-text-soft)", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(row.partner_fee_usd || 0)}</div>
                    <div style={{ width: 60, textAlign: "right", color: "var(--bl-text-soft)", fontSize: 11 }}>
                      {Number(row.swap_count || 0).toLocaleString()}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
      )}

      {/* ── Earnings by route — consolidated across every integration ──── */}
      {!isAnalytics && (
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>Earnings by route</h3>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total earnings · {timeframe === "all" ? "all time" : `last ${timeframe}`}</div>
            <div style={{ fontSize: "1.25rem", color: "#5be58a", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(totalRouteFee)}</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "var(--bl-text-soft)", marginBottom: 12, lineHeight: 1.5 }}>
          What we actually earn, split by the route each swap took — across every integration.
          SwapKit is broken into its routed sub-providers (official figures); Rango, CowSwap, Jupiter and
          Exolix each count as their own route. Routes that share a name merge, so you can see when two
          integrations lean on the same underlying route.
        </div>
        {summaryLoading ? (
          <Skeleton height={120} />
        ) : routeRows.length === 0 ? (
          <div style={{ padding: "1rem", textAlign: "center", color: "var(--bl-text-soft)", fontSize: 13 }}>
            No routed earnings in this period yet.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <div style={{ width: 130 }}>Route</div>
              <div style={{ flex: 1 }}>Share of earnings</div>
              <div style={{ width: 90 }}>Via</div>
              <div style={{ width: 110, textAlign: "right" }}>Earnings</div>
            </div>
            {routeRows.map((r, i) => {
              const pct = totalRouteFee > 0 ? (r.fee / totalRouteFee) * 100 : 0;
              const c = providerColor(r.route, i);
              return (
                <div key={r.route} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 130, fontSize: 12, color: "#fff", fontWeight: 600, display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ width: 9, height: 9, borderRadius: 3, background: c, flexShrink: 0 }} />
                    {r.route}
                  </div>
                  <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: c }} />
                  </div>
                  <div style={{ width: 90, display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {r.vias.map((v) => (
                      <span key={v} title={`via ${v}`} style={{ fontSize: 9, fontWeight: 600, padding: "1px 6px", borderRadius: 999, background: `${providerColor(v)}22`, color: providerColor(v), whiteSpace: "nowrap" }}>{v}</span>
                    ))}
                  </div>
                  <div style={{ width: 110, textAlign: "right", color: r.fee > 0 ? "#5be58a" : "var(--bl-text-soft)", fontSize: 13, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmtUSD(r.fee)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* ── Fees by network ───────────────────────────────────────────── */}
      {!isAnalytics && (
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>Fee revenue by network</h3>
          <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>{timeframe === "all" ? "all time" : `last ${timeframe}`}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--bl-text-soft)", marginBottom: 10, lineHeight: 1.5 }}>
          The share bar on each network is colour-coded by integration, so you can see which integration is
          driving usage on each chain (by volume).
        </div>
        {networkProviders.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
            {networkProviders.map((p) => (
              <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--bl-text-soft)" }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: providerColor(p), flexShrink: 0 }} />
                {p}
              </span>
            ))}
          </div>
        )}
        {summaryLoading ? (
          <Skeleton height={120} />
        ) : byNetwork.length === 0 ? (
          <div style={{ padding: "1rem", textAlign: "center", color: "var(--bl-text-soft)", fontSize: 13 }}>
            No captured-fee swaps in this period.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 10, color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <div style={{ width: 90 }}>Network</div>
              <div style={{ flex: 1 }}>Integration usage</div>
              <div style={{ width: 110, textAlign: "right" }}>Our fee</div>
              <div style={{ width: 110, textAlign: "right" }}>SwapKit fee</div>
              <div style={{ width: 110, textAlign: "right" }}>Volume</div>
              <div style={{ width: 60, textAlign: "right" }}>Swaps</div>
            </div>
            {byNetwork
              .slice()
              .sort((a, b) => Number(b.our_fee_usd) - Number(a.our_fee_usd))
              .map((row) => {
                const v = Number(row.our_fee_usd || 0);
                // Colour-coded segments: split this network's bar by each
                // integration's share of the network's volume (usage).
                const segs = (networkSegments.get(row.network) || [])
                  .slice()
                  .sort((a, b) => Number(b.volume_usd) - Number(a.volume_usd));
                const segTotal = segs.reduce((a, s) => a + Number(s.volume_usd || 0), 0);
                return (
                  <div key={row.network} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 90, fontSize: 12, color: "#fff", fontWeight: 600 }}>{row.network}</div>
                    <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.05)", borderRadius: 999, overflow: "hidden", display: "flex" }}>
                      {segTotal > 0
                        ? segs.map((s) => (
                            <div
                              key={s.swap_partner}
                              title={`${s.swap_partner}: ${fmtUSD(s.volume_usd)} volume`}
                              style={{ width: `${(Number(s.volume_usd || 0) / segTotal) * 100}%`, height: "100%", background: providerColor(s.swap_partner) }}
                            />
                          ))
                        : null}
                    </div>
                    <div style={{ width: 110, textAlign: "right", color: "#5be58a", fontSize: 13, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmtUSD(v)}</div>
                    <div style={{ width: 110, textAlign: "right", color: "var(--bl-text-soft)", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(row.swapkit_fee_usd || 0)}</div>
                    <div style={{ width: 110, textAlign: "right", color: "var(--bl-text-soft)", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{fmtUSD(row.volume_usd || 0)}</div>
                    <div style={{ width: 60, textAlign: "right", color: "var(--bl-text-soft)", fontSize: 11 }}>
                      {Number(row.swap_count || 0).toLocaleString()}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
      )}

      {/* ════════ ANALYTICS VIEW ════════ */}
      {isAnalytics && (
      <>
      {/* ── App activity (fixed window — NOT driven by the timeframe above) ── */}
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, color: "#fff", fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", fontWeight: 600 }}>App activity</h3>
          <span style={{ fontSize: 11, color: "var(--bl-text-soft)" }}>overall totals · independent of the timeframe filter</span>
        </div>

        {/* KPI row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
          <KpiCard loading={activityLoading} label="Transactions"      value={fmt(totalTx)}            icon="ri-exchange-dollar-line" accent="var(--bl-accent)" />
          <KpiCard loading={activityLoading} label="On-ramp (Paybis)"  value={fmt(actPaybis.on_ramp)}  icon="ri-arrow-down-line"      accent="var(--bl-success)" />
          <KpiCard loading={activityLoading} label="Off-ramp (Paybis)" value={fmt(actPaybis.off_ramp)} icon="ri-arrow-up-line"        accent="var(--bl-accent-light)" />
          <KpiCard loading={activityLoading} label="Cards created"     value={fmt(actMetrics?.cards_created_count ?? actOverview?.card_statistics?.total_cards)} icon="ri-bank-card-line" accent="var(--bl-accent)" />
        </div>

        {/* Chart + Distribution row */}
        <div className="fees-activity-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: "1rem" }}>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--bl-line)", borderRadius: 12, padding: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <h4 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "0.95rem", color: "#fff", fontWeight: 600 }}>Transactions, last 14 days</h4>
              <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>combined</span>
            </div>
            {activityLoading ? (
              <Skeleton height={140} radius={8} />
            ) : activitySeries.length === 0 || activitySeries.every((p) => p.y === 0) ? (
              <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--bl-text-soft)", flexDirection: "column", gap: 4 }}>
                <i className="ri-line-chart-line" style={{ fontSize: 28, opacity: 0.5 }} />
                <span>No transactions in the last 14 days</span>
              </div>
            ) : (
              <>
                <MiniLineChart points={activitySeries} />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "var(--bl-text-soft)" }}>
                  {activitySeries.filter((_, i) => i % Math.ceil(activitySeries.length / 7) === 0).map((p, i) => (
                    <span key={i}>{p.label}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--bl-line)", borderRadius: 12, padding: "1.25rem" }}>
            <h4 style={{ margin: 0, marginBottom: "1rem", fontFamily: "var(--bl-font-display)", fontSize: "0.95rem", color: "#fff", fontWeight: 600 }}>Distribution</h4>
            {activityLoading ? (
              <Skeleton height={160} width={160} radius={80} />
            ) : activityDonut.length === 0 ? (
              <div style={{ color: "var(--bl-text-soft)", fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <i className="ri-pie-chart-2-line" style={{ fontSize: 22, opacity: 0.5 }} />
                No transactions in this window
              </div>
            ) : (
              <Donut slices={activityDonut} />
            )}
          </div>
        </div>
      </div>

      {/* ── Recent activity (moved from Dashboard; fixed window) ──────── */}
      <div className="bl-glass" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "1rem" }}>
          <h3 style={{ margin: 0, fontFamily: "var(--bl-font-display)", fontSize: "1.05rem", color: "#fff", fontWeight: 600 }}>Recent activity</h3>
          <span style={{ fontSize: 12, color: "var(--bl-text-soft)" }}>
            {activityLoading ? <Spinner size={12} /> : `${(actOverview?.recent_transactions || []).length} entries`}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)", borderBottom: "1px solid var(--bl-line)" }}>
                {["Type", "Amount", "Network", "Status", "When"].map((h, i) => (
                  <th key={i} style={{
                    padding: "0.65rem 1rem", textAlign: "left", fontSize: 11,
                    color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activityLoading && Array.from({ length: 4 }).map((_, i) => (
                <tr key={`skel-${i}`} style={{ borderBottom: "1px solid var(--bl-line)" }}>
                  <td style={{ padding: "0.75rem 1rem" }}><Skeleton height={14} width="60%" /></td>
                  <td style={{ padding: "0.75rem 1rem" }}><Skeleton height={14} width="80%" /></td>
                  <td style={{ padding: "0.75rem 1rem" }}><Skeleton height={14} width="50%" /></td>
                  <td style={{ padding: "0.75rem 1rem" }}><Skeleton height={20} width={70} radius={6} /></td>
                  <td style={{ padding: "0.75rem 1rem" }}><Skeleton height={14} width={60} /></td>
                </tr>
              ))}
              {!activityLoading && (actOverview?.recent_transactions || []).map((t) => {
                const status = t.status ? String(t.status).toUpperCase() : "";
                const tint = STATUS_TINT[status] || { bg: "rgba(255,255,255,0.04)", fg: "var(--bl-text-soft)" };
                // Amount keeps its own token symbol; chain shows in its own column.
                const token = t.initial_token || t.recipient_token || "";
                const amt = t.amount != null ? Number(t.amount).toLocaleString(undefined, { maximumFractionDigits: 6 }) : "—";
                return (
                  <tr key={t.id} style={{ borderBottom: "1px solid var(--bl-line)" }}>
                    <td style={{ padding: "0.75rem 1rem", color: "#fff", fontSize: 14 }}>{t.tx_type}</td>
                    <td style={{ padding: "0.75rem 1rem", color: "var(--bl-text-strong)", fontSize: 14, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                      {amt}{token && <span style={{ color: "var(--bl-text-soft)", marginLeft: 5 }}>{token}</span>}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", fontSize: 13 }}>
                      <span style={{ color: t.network_color || "var(--bl-text-soft)", fontWeight: 600 }}>{t.network_display || t.network}</span>
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 11, background: tint.bg, color: tint.fg }}>{status || t.status}</span>
                    </td>
                    <td style={{ padding: "0.75rem 1rem", color: "var(--bl-text-soft)", fontSize: 13 }}>{relTime(t.timestamp)}</td>
                  </tr>
                );
              })}
              {!activityLoading && (actOverview?.recent_transactions || []).length === 0 && (
                <tr><td colSpan={5} style={{ padding: "1.5rem", textAlign: "center", color: "var(--bl-text-soft)" }}>No recent activity</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {/* ── Settlements / Missing fees (Fees view only) ───────────────── */}
      {!isAnalytics && (
      <>
      {/* ── Tab selector ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, marginBottom: "0.5rem" }}>
        {[{ id: "settlements", label: `Settlements (${fmtNum(settlementsTotal)})` },
          { id: "missing",     label: `Missing fees (${fmtNum(missingTotal)})` }].map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "8px 8px 0 0",
                background: active ? "var(--bl-panel, #15172b)" : "rgba(255,255,255,0.03)",
                border: "1px solid var(--bl-line)",
                borderBottom: active ? "1px solid var(--bl-panel, #15172b)" : "1px solid var(--bl-line)",
                color: active ? "#fff" : "var(--bl-text-soft)",
                fontSize: 13, fontWeight: 600,
                cursor: "pointer",
              }}
            >{t.label}</button>
          );
        })}
      </div>

      {/* ── Tab body ──────────────────────────────────────────────────── */}
      {tab === "settlements" ? (
        <SettlementsTable
          rows={settlements}
          total={settlementsTotal}
          loading={settlementsLoading}
          page={settlementsPage}
          pageSize={PAGE_SIZE}
          onPage={setSettlementsPage}
        />
      ) : (
        <MissingTable
          rows={missing}
          total={missingTotal}
          loading={missingLoading}
          page={missingPage}
          pageSize={PAGE_SIZE}
          onPage={setMissingPage}
        />
      )}
      </>
      )}
    </div>
  );
};

const th = {
  textAlign: "left", padding: "0.65rem 1rem",
  fontSize: 11, fontWeight: 600,
  color: "var(--bl-text-soft)", textTransform: "uppercase", letterSpacing: "0.06em",
  whiteSpace: "nowrap",
};
const td = { padding: "0.7rem 1rem", verticalAlign: "middle" };
const pagerBtn = (disabled) => ({
  padding: "5px 10px", borderRadius: 6,
  background: "rgba(255,255,255,0.04)", border: "1px solid var(--bl-line)",
  color: disabled ? "var(--bl-text-soft)" : "#fff",
  fontSize: 12, fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
});

function Pager({ page, total, pageSize, onPage, loading }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div style={{
      padding: "0.75rem 1rem", borderTop: "1px solid var(--bl-line)",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      fontSize: 12, color: "var(--bl-text-soft)",
    }}>
      <span>
        {total === 0 ? "0 rows" : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`}
        {loading && <Spinner size={11} style={{ marginLeft: 8, verticalAlign: "middle" }} />}
      </span>
      <div style={{ display: "inline-flex", gap: 6 }}>
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1 || loading} style={pagerBtn(page <= 1 || loading)}>Prev</button>
        <span style={{ padding: "5px 10px", color: "var(--bl-text-soft)" }}>{page} / {totalPages}</span>
        <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages || loading} style={pagerBtn(page >= totalPages || loading)}>Next</button>
      </div>
    </div>
  );
}

function SettlementsTable({ rows, total, loading, page, pageSize, onPage }) {
  return (
    <div className="bl-glass" style={{ overflow: "hidden", borderRadius: "0 8px 8px 8px" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.03)" }}>
              <th style={th}>Received</th>
              <th style={th}>Network</th>
              <th style={th}>Amount (USD)</th>
              <th style={th}>Amount (native)</th>
              <th style={th}>Token</th>
              <th style={th}>Tx hash</th>
              <th style={th}>From</th>
              <th style={th}>Source</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}><td colSpan={8} style={{ padding: "0.75rem 1rem" }}><Skeleton height={18} /></td></tr>
              ))
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} style={{ padding: "2rem 1rem", textAlign: "center", color: "var(--bl-text-soft)" }}>
                No fee settlements recorded yet. The wallet monitor in biorlabs-backend hasn't posted anything (or isn't running yet).
              </td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} style={{ borderTop: "1px solid var(--bl-line)" }}>
                <td style={td}><span style={{ color: "#fff", fontVariantNumeric: "tabular-nums" }}>{fmtDate(row.received_at)}</span></td>
                <td style={td}><span style={{ color: "#fff", fontWeight: 600 }}>{row.network}</span></td>
                <td style={{ ...td, color: "#5be58a", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{row.amount_usd != null ? fmtUSD(row.amount_usd) : "—"}</td>
                <td style={{ ...td, color: "#fff", fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{row.amount_native ?? "—"}</td>
                <td style={td}>{row.token_symbol || <span style={{ color: "var(--bl-text-soft)" }}>—</span>}</td>
                <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontSize: 12, color: "var(--bl-text-soft)" }}>{shortHash(row.tx_hash)}</td>
                <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontSize: 12, color: "var(--bl-text-soft)" }}>{shortHash(row.from_address)}</td>
                <td style={{ ...td, color: "var(--bl-text-soft)", fontSize: 11 }}>{row.source || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={total} pageSize={pageSize} onPage={onPage} loading={loading} />
    </div>
  );
}

function MissingTable({ rows, total, loading, page, pageSize, onPage }) {
  return (
    <div className="bl-glass" style={{ overflow: "hidden", borderRadius: "0 8px 8px 8px" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.03)" }}>
              <th style={th}>When</th>
              <th style={th}>Network</th>
              <th style={th}>Partner</th>
              <th style={th}>Pair</th>
              <th style={th}>Input USD</th>
              <th style={th}>Output USD</th>
              <th style={th}>Network fee</th>
              <th style={th}>Markup</th>
              <th style={th}>Tx hash</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}><td colSpan={9} style={{ padding: "0.75rem 1rem" }}><Skeleton height={18} /></td></tr>
              ))
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} style={{ padding: "2rem 1rem", textAlign: "center", color: "var(--bl-text-soft)" }}>
                No swaps flagged as missing fees. Every captured swap is earning.
              </td></tr>
            )}
            {rows.map((row) => {
              const pair = row.initial_token && row.recipient_token ? `${row.initial_token} → ${row.recipient_token}` : "—";
              return (
                <tr key={row.id} style={{ borderTop: "1px solid var(--bl-line)" }}>
                  <td style={td}><span style={{ color: "#fff", fontVariantNumeric: "tabular-nums" }}>{fmtDate(row.timestamp)}</span></td>
                  <td style={td}><span style={{ color: "#fff", fontWeight: 600 }}>{row.network}</span></td>
                  <td style={td}>{row.swap_partner || <span style={{ color: "var(--bl-text-soft)" }}>—</span>}</td>
                  <td style={td}>{pair}</td>
                  <td style={{ ...td, color: "#fff", fontVariantNumeric: "tabular-nums" }}>{row.input_value_usd != null ? fmtUSD(row.input_value_usd) : "—"}</td>
                  <td style={{ ...td, color: "#fff", fontVariantNumeric: "tabular-nums" }}>{row.output_value_usd != null ? fmtUSD(row.output_value_usd) : "—"}</td>
                  <td style={{ ...td, color: "var(--bl-text-soft)", fontVariantNumeric: "tabular-nums" }}>{row.network_fee_usd != null ? fmtUSD(row.network_fee_usd) : "—"}</td>
                  <td style={{ ...td, color: "#f3af4c", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{row.total_markup_usd != null ? fmtUSD(row.total_markup_usd) : "—"}</td>
                  <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontSize: 12, color: "var(--bl-text-soft)" }}>{shortHash(row.tx_hash)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager page={page} total={total} pageSize={pageSize} onPage={onPage} loading={loading} />
    </div>
  );
}

export default Fees;
